import { useState } from 'react';
import type { SmartViewBasemap } from './SmartView';
import type { BasemapPosition, ProjectBasemap } from '../../utils/basemap';
import styles from './BasemapRail.module.css';

interface BasemapRailProps {
  basemap: SmartViewBasemap | null;
  onChange: (patch: Partial<ProjectBasemap>) => void;
  onPlace: () => void;
  onSave: () => void;
  onRevert: () => void;
  onNudge: (forward: number, right: number, turn: number) => void;
  onResetTune: () => void;
}

const HEIGHT_RANGE = 30;
const STEPS = [
  { label: '0.1 m · 0.1°', move: 0.1, turn: 0.1 },
  { label: '1 m · 0.5°', move: 1, turn: 0.5 },
  { label: '10 m · 2°', move: 10, turn: 2 },
];

const BasemapRail = ({ basemap, onChange, onPlace, onSave, onRevert, onNudge, onResetTune }: BasemapRailProps) => {
  const [stepIndex, setStepIndex] = useState(0);
  if (!basemap) return null;
  const { prefs, providers, state, status, canEdit } = basemap;
  const on = prefs.provider !== 'off';
  const autoHeight = status.autoHeight;
  const heightOffset = prefs.height !== null && autoHeight !== null ? prefs.height - autoHeight : 0;
  const frameText = basemap.frame === 'crs'
    ? `Grid: EPSG:${basemap.epsg}`
    : basemap.frame === 'placement'
      ? `Placed on the map · rotated ${Math.round(basemap.placement?.heading ?? 0)}°`
      : basemap.frame === 'pin'
        ? 'At the project location · north up'
        : null;
  const mismatch = basemap.gridFit === 'local'
    ? `This scan uses its own local coordinates, not EPSG:${basemap.epsg}`
    : `In EPSG:${basemap.epsg} this scan would be more than 20 km from the project location`;
  const gridHint = basemap.gridFit === 'fits' || basemap.frame === 'placement' ? null
    : basemap.frame === 'crs' ? `${mismatch}, so the map may be in the wrong place.`
      : basemap.frame === 'pin'
        ? prefs.position === 'auto' ? `${mismatch}, so the map is placed at the project location. Fine-tune it to line it up.` : null
        : `${mismatch}, so ${canEdit ? 'place the site on the map.' : 'no map can be drawn.'}`;
  const { sources } = basemap;
  const autoLabel = sources.auto === 'crs' ? 'file coordinates' : sources.auto ? 'project pin' : 'none';
  const step = STEPS[stepIndex];
  const adj = basemap.gridAdjust;
  const tuneText = basemap.frame === 'crs'
    ? adj ? `Moved ${Math.hypot(adj.dx, adj.dy).toFixed(1)} m · turned ${adj.rotation.toFixed(1)}°` : 'Not adjusted'
    : basemap.frame === 'placement' ? `Turned ${Math.round((basemap.placement?.heading ?? 0) * 10) / 10}° from north` : 'Not adjusted';
  const pad: { icon: string; title: string; forward: number; right: number; turn: number }[] = [
    { icon: 'rotate_left', title: 'Turn the map left', forward: 0, right: 0, turn: step.turn },
    { icon: 'arrow_upward', title: 'Move the map away from you', forward: step.move, right: 0, turn: 0 },
    { icon: 'rotate_right', title: 'Turn the map right', forward: 0, right: 0, turn: -step.turn },
    { icon: 'arrow_back', title: 'Move the map left', forward: 0, right: -step.move, turn: 0 },
    { icon: 'arrow_downward', title: 'Move the map towards you', forward: -step.move, right: 0, turn: 0 },
    { icon: 'arrow_forward', title: 'Move the map right', forward: 0, right: step.move, turn: 0 },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>map</span>
        <span className={styles.label}>Base map</span>
        {on && status.active && (
          <span className={styles.count} title="Map tiles on screen">
            {status.loading > 0 ? `${status.tiles} · loading` : status.tiles}
          </span>
        )}
      </div>
      <div className={styles.row}>
        <select
          className={styles.select}
          value={prefs.provider}
          disabled={!canEdit && !on}
          onChange={(e) => onChange({ provider: e.target.value })}
        >
          <option value="off">Off</option>
          {providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>
      {on && canEdit && (sources.file || sources.pin) && (
        <label className={styles.row}>
          <span className={styles.rowLabel}>Position</span>
          <select
            className={styles.select}
            value={prefs.position}
            onChange={(e) => onChange({ position: e.target.value as BasemapPosition })}
          >
            <option value="auto">Automatic · {autoLabel}</option>
            {sources.file && <option value="file">File coordinates (EPSG:{basemap.epsg})</option>}
            {sources.pin && <option value="pin">Project pin</option>}
          </select>
        </label>
      )}
      {on && gridHint && <div className={styles.hint}>{gridHint}</div>}
      {on && state === 'noFrame' && !gridHint && (
        <div className={styles.hint}>
          {canEdit
            ? 'This project has no coordinate system. Set its EPSG in the project settings, or place the site on the map.'
            : 'This project has no coordinate system, so no map can be drawn.'}
        </div>
      )}
      {on && state === 'noProvider' && <div className={styles.hint}>This map provider is not available any more.</div>}
      {on && status.error && <div className={styles.hintError}>{status.error}</div>}
      {on && status.failed > 0 && (
        <div className={styles.hintError}>
          {status.failed} map {status.failed === 1 ? 'tile' : 'tiles'} could not be loaded{status.lastError ? ` (${status.lastError})` : ''}; coarser tiles stand in.
          {prefs.provider === 'osm' && ' OpenStreetMap limits heavy use: an Esri or Google key (Platform Settings → Base Map) avoids this.'}
        </div>
      )}
      {on && state === 'on' && (
        <>
          <label className={styles.row}>
            <span className={styles.rowLabel}>Opacity</span>
            <input
              type="range" min={0.1} max={1} step={0.05}
              className={styles.range}
              value={prefs.opacity}
              onChange={(e) => onChange({ opacity: parseFloat(e.target.value) })}
            />
            <span className={styles.value}>{Math.round(prefs.opacity * 100)}%</span>
          </label>
          <label className={styles.row} title="Lift or lower the map at the site; 0 puts it just under the scene's ground">
            <span className={styles.rowLabel}>Height</span>
            <input
              type="range" min={-HEIGHT_RANGE} max={HEIGHT_RANGE} step={0.1}
              className={styles.range}
              value={Math.max(-HEIGHT_RANGE, Math.min(HEIGHT_RANGE, heightOffset))}
              disabled={autoHeight === null}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                onChange({ height: v === 0 || autoHeight === null ? null : autoHeight + v });
              }}
            />
            <span className={styles.value}>{prefs.height === null ? 'auto' : `${heightOffset >= 0 ? '+' : ''}${heightOffset.toFixed(1)} m`}</span>
          </label>
          {basemap.terrainAvailable && (
            <label className={styles.row}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={prefs.terrain}
                onChange={(e) => onChange({ terrain: e.target.checked })}
              />
              <span className={styles.rowLabel} style={{ width: 'auto' }}>Terrain</span>
            </label>
          )}
        </>
      )}
      {frameText && <div className={styles.frame}>{frameText}</div>}
      {on && state === 'on' && canEdit && basemap.frame && (
        <div className={styles.tune}>
          <div className={styles.tuneHead}>
            <span className={styles.rowLabel} style={{ width: 'auto' }}>Fine-tune</span>
            <select className={styles.stepSelect} value={stepIndex} onChange={(e) => setStepIndex(Number(e.target.value))} title="Step per click">
              {STEPS.map((s, i) => <option key={s.label} value={i}>{s.label}</option>)}
            </select>
          </div>
          <div className={styles.pad}>
            {pad.map((b) => (
              <button
                key={b.icon} type="button" className={styles.padBtn} title={b.title} aria-label={b.title}
                onClick={() => onNudge(b.forward, b.right, b.turn)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{b.icon}</span>
              </button>
            ))}
          </div>
          <div className={styles.tuneFoot}>
            <span className={styles.frame}>{tuneText}</span>
            {basemap.tuned && <button type="button" className={styles.linkBtn} onClick={onResetTune}>Reset</button>}
          </div>
        </div>
      )}
      {canEdit && basemap.frame !== 'crs' && (
        <button type="button" className={styles.placeBtn} onClick={onPlace}>
          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>pin_drop</span>
          {basemap.frame === 'placement' ? 'Adjust placement' : basemap.frame === 'pin' ? 'Move pin on map…' : 'Place on map…'}
        </button>
      )}
      {canEdit && basemap.unsaved && (
        <div className={styles.unsaved}>
          <div className={styles.hint}>Only in this view. Save to make it the project's map for everyone.</div>
          <div className={styles.saveRow}>
            <button type="button" className={styles.saveBtn} onClick={onSave} disabled={basemap.saving}>
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>save</span>
              {basemap.saving ? 'Saving…' : 'Save for project'}
            </button>
            <button type="button" className={styles.placeBtn} onClick={onRevert} disabled={basemap.saving}>
              Revert
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasemapRail;
